import {FormControl, InputLabel, MenuItem, Select, TextField} from "@mui/material";
import React from "react";
import { InputEstimateTreeOperationDto } from "../dtos/estimate-tree-operation.dto";

export const InputDataType = {
  ShortText: 'shortText',
  LongText: 'longText',
  Number: 'number',
  Percentage: 'percentage'
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const + companion type pattern
export type InputDataType = typeof InputDataType[keyof typeof InputDataType];

export interface InputSchema {
  name: string;
  displayName?: string;
  dataType: InputDataType;
  options?: string[] | number[];
  unit?: string;
  required: boolean;
  conditionalDisplay?: (operation: InputEstimateTreeOperationDto | undefined) => boolean;
  noDefaultValue?: boolean;
  /** A selectable, labelled empty choice (e.g. "Unknown"); replaces the placeholder. */
  emptyLabel?: string;
}

export const buildInput = (schema: InputSchema, valueProducer: () => (number | string), onChange: (value: string | number) => void, disabled?: boolean) => {
  switch (schema.dataType) {
    case InputDataType.ShortText:
      return schema.options ? buildShortTextSelectInput(schema, valueProducer as () => string, onChange, disabled) : buildShortTextInput(schema, valueProducer as () => string, onChange, disabled);
    case InputDataType.LongText:
      return buildLongTextInput(schema, valueProducer as () => string, onChange, disabled);
    case InputDataType.Number:
      return schema.options ? buildNumberSelectInput(schema, valueProducer as () => number, onChange, disabled) : buildNumberInput(schema, valueProducer as () => number, onChange, disabled);
    case InputDataType.Percentage:
      return buildPercentageInput(schema, valueProducer as () => number, onChange, disabled);
    default:
      throw new Error(`Unknown data type: ${schema.dataType}`);
  }
}

const buildShortTextInput = (schema: InputSchema, valueProducer: () => string, onChange: (value: string) => void, disabled?: boolean) => {
  return (
    <FormControl key={schema.name} fullWidth data-review-field={schema.name}>
      <TextField
        disabled={disabled}
        label={schema.displayName ?? schema.name}
        variant="outlined"
        value={valueProducer()}
        onChange={e => onChange(e.target.value)}
        required={schema.required}
      />
    </FormControl>)
}

const buildShortTextSelectInput = (schema: InputSchema, valueProducer: () => string, onChange: (value: string) => void, disabled?: boolean) => {
  const currentValue = valueProducer();
  const emptyAdditionalText = schema.noDefaultValue && !currentValue ? ' (Choose option)' : '';
  return (
    <FormControl key={schema.name} fullWidth required={schema.required} data-review-field={schema.name}>
      <InputLabel id={`${schema.name}-label`}>{schema.displayName ?? schema.name}{emptyAdditionalText}</InputLabel>
      <Select
        labelId={`${schema.name}-label`}
        disabled={disabled}
        label={schema.displayName ?? schema.name}
        value={currentValue}
        onChange={e => onChange(e.target.value as string)}
      >
        {generateMenuItemsFromValues(schema.options as string[], schema.unit ?? '', schema.noDefaultValue, schema.emptyLabel)}
      </Select>
    </FormControl>)
}

const buildLongTextInput = (schema: InputSchema, valueProducer: () => string, onChange: (value: string) => void, disabled?: boolean) => {
  return (
    <FormControl key={schema.name} fullWidth data-review-field={schema.name}>
      <TextField
        disabled={disabled}
        label={schema.displayName ?? schema.name}
        variant="outlined"
        multiline
        inputProps={{autoCapitalize: 'sentences'}}
        rows={2}
        value={valueProducer()}
        onChange={e => onChange(e.target.value)}
        required={schema.required}
      />
    </FormControl>)
}

const buildNumberInput = (schema: InputSchema, valueProducer: () => number, onChange: (value: number) => void, disabled?: boolean) => {
  return (
    <FormControl key={schema.name} fullWidth data-review-field={schema.name}>
      <TextField
        disabled={disabled}
        label={schema.displayName ?? schema.name}
        variant="outlined"
        value={valueProducer()}
        onChange={e => onChange(parseFloat(e.target.value))}
        required={schema.required}
      />
    </FormControl>)
}

const buildNumberSelectInput = (schema: InputSchema, valueProducer: () => number, onChange: (value: number) => void, disabled?: boolean) => {
  const currentValue = valueProducer();
  const emptyAdditionalText = schema.noDefaultValue && !currentValue ? ' (Choose option)' : '';
  return (
    <FormControl key={schema.name} fullWidth required={schema.required} data-review-field={schema.name}>
      <InputLabel id={`${schema.name}-label`}>{schema.displayName ?? schema.name}{emptyAdditionalText}</InputLabel>
      <Select
        labelId={`${schema.name}-label`}
        disabled={disabled}
        label={schema.displayName ?? schema.name}
        value={currentValue}
        onChange={e => onChange(e.target.value as number)}
      >
        {generateMenuItemsFromValues(schema.options as number[], ` ${schema.unit ?? ""}`, schema.noDefaultValue, schema.emptyLabel)}
      </Select>
    </FormControl>)
}

const buildPercentageInput = (schema: InputSchema, valueProducer: () => number, onChange: (value: number) => void, disabled?: boolean) => {
  const currentValue = valueProducer().toString() ?? '';
  const emptyAdditionalText = schema.noDefaultValue && !currentValue ? ' (Choose option)' : '';
  return (
  <FormControl key={schema.name} fullWidth required={schema.required} data-review-field={schema.name}>
    <InputLabel id={`${schema.name}-label`}>{schema.displayName ?? schema.name}{emptyAdditionalText}</InputLabel>
    <Select
      labelId={`${schema.name}-label`}
      disabled={disabled}
      label={schema.displayName ?? schema.name}
      value={currentValue}
      onChange={e => onChange(parseInt(e.target.value))}
    >
      {generateMenuItemsFromValues(Array.from(Array(100).keys()).slice(0, 100), '%', schema.noDefaultValue, schema.emptyLabel)}
    </Select>
  </FormControl>)
}



const generateMenuItemsFromValues = (values: number[] | string[], unit: string, noDefaultValue?: boolean, emptyLabel?: string) => {
  const items = values.map((value) => {
    return <MenuItem value={value} key={value}>{value}{unit}</MenuItem>
  });

  if (emptyLabel) {
    return [<MenuItem value={''} key={'EMPTY'}><em>{emptyLabel}</em></MenuItem>, ...items];
  }
  
  if (noDefaultValue) {
    const placeholder = <MenuItem value={''} key={'PLACEHOLDER'} disabled>
      <em>Choose option</em>
    </MenuItem>
    return [placeholder, ...items];
  }
  
  // Include empty option for normal behavior
  const empty = <MenuItem value={''} key={'EMPTY'}></MenuItem>
  return [empty, ...items];
}
